import { useEffect, useState, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Notify } from "notiflix";
import Notiflix from "notiflix";
import { useNavigate } from "react-router-dom";
import { useBookingContext } from "../context/BookingContext";
import usePost from "./usePost";
import usePostMultipleImage from "./usePostMultipleImage";
import { sumCalculatePrice } from "../utils";
import useUploadImage from "./useUploadImage";
import api from "../services/api";
import useFetch from "./useFetch";

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const SUPPORTED_FORMATS = ["image/jpeg", "image/png", "image/jpg"];

export const useCustomerForm = () => {
  const { modal, setModal, data, aditionalItem, dateUsage } = useBookingContext();
  const [identity, setIdentity] = useState("");
  const [identityImage, setIdentityImage] = useState(null);
  const [drivingLicense, setDrivingLicense] = useState(null);
  const [familyCard, setFamilyCard] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [flight, setFlight] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSuccess, setIsSuccess] = useState(false);
  const [priceCalculate, setPriceCalculate] = useState(null);
  const [paymentLink, setPaymentLink] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [bookingData, setBookingData] = useState(null);

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const navigate = useNavigate();

  const findPrice = usePost({ module: `v1/car-prices/calculate` });
  const createBooking = usePost({ module: "bookings" });
  const createPayment = usePost({ module: "xendit/invoice" });

  const dlvPrice = priceCalculate?.prices?.find((v) => v.type === "delivery_fee");
  const pcpPrice = priceCalculate?.prices?.find((v) => v.type === "pickup_fee");
  const totalSum = sumCalculatePrice(priceCalculate?.prices || []);

  const { upload: uploadImage, isLoading: uploadImageLoading, error: uploadError } = useUploadImage();
  const { uploadMultipleImage } = usePostMultipleImage();
  const createCustomer = usePost({ module: "customers" });

  const { items: insuranceData, isLoading: insuranceLoading } = useFetch({
    module: "insurances",
    enabled: true,
  });

  // Formik configuration
  const formik = useFormik({
    initialValues: {
      identity: "",
      driving_license: "",
      identity_image: "",
      family_card: "",
      social_media: "",
      business_id: "",
      name: "",
      email: "",
      phone: "",
      address: "",
      insurance_id: "basic_protection",
      insurance_code: "basic_protection",
      additional_informations: {
        emergencyContact1: "",
        emergencyContact2: "",
        flightReservation: [],
        hotelReservation: [],
      },
      additional_items: [],
      notes: "",
      privacyPolicy: false,
    },
    validationSchema: Yup.object().shape({
      identity:
        data?.type === "self_drive" ? Yup.string().required("Identity type is required") : Yup.string().notRequired(),
      identity_image:
        data?.type === "self_drive" ? Yup.mixed().required("Identity image is required") : Yup.mixed().notRequired(),

      driving_license: Yup.mixed().test("custom-validation", "Driving license is required", function (value) {
        if (data?.type === "self_drive") {
          return value;
        } else {
          return true;
        }
      }),

      family_card: Yup.mixed().optional(),
      social_media: Yup.string().required("Social media is required"),
      business_id: Yup.mixed().optional(),
      name: Yup.string().required("Name is required"),
      email: Yup.string()
        .email("Enter valid email")
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Enter valid email")
        .required("Email is required"),
      phone: Yup.string().required("Whatsapp number is required").matches(phoneRegExp, "Enter valid Whatsapp number"),
      address: Yup.string().required("Address is required"),
      privacyPolicy: Yup.boolean().oneOf([true], "You must accept the privacy policy"),
      additional_informations:
        data?.type === "self_drive"
          ? Yup.object({
              emergencyContact1: Yup.number().required("Emergency contact 1 is required"),
              emergencyContact2: Yup.number().required("Emergency contact 2 is required"),
            })
          : Yup.object().notRequired(),
    }),

    onSubmit: async (v) => {
      if (bookingData) {
        setCurrentStep(5);
        return;
      }

      setDisabled(true);
      Notiflix.Loading.hourglass();

      try {
        const idImg = v.identity_image ? await uploadImage(v.identity_image) : null;
        const drvImg = data?.type === "self_drive" && v.driving_license ? await uploadImage(v.driving_license) : null;
        const famImg = data?.type === "self_drive" && v.family_card ? await uploadImage(v.family_card) : null;
        const bizImg = data?.type === "self_drive" && v.business_id ? await uploadImage(v.business_id) : null;

        const flightImg = await uploadMultipleImage(v.flight);
        const hotelImg = await uploadMultipleImage(v.hotel);

        const rebuiltFlight = flightImg?.map((item) => item?.value?.data?.url) || [];
        const rebuiltHotel = hotelImg?.map((item) => item?.value?.data?.url) || [];

        const customerData = {
          ...v,
          identity_image: [idImg?.url],
          ...(drvImg ? { driving_license: [drvImg?.url] } : {}),
          ...(famImg ? { family_card: [famImg?.url] } : {}),
          ...(bizImg ? { business_id: [bizImg?.url] } : {}),
        };

        const resCustomer = await createCustomer.mutateAsync(customerData);
        const csId = resCustomer?.data?.data?.id;

        const bookingFormData = {
          type: data?.type,
          tour_package: data?.tourPackage,
          duration: data.duration || null,
          car_id: data?.car?.id,
          customer_id: csId,
          price: totalSum,
          pickup_date: data?.pickup_date,
          dropoff_date: data?.dropoff_date,
          pickup_location: data?.pickup_location,
          dropoff_location: data?.dropoff_location,
          pickup_location_json: data?.pickup_location_json,
          dropoff_location_json: data?.dropoff_location_json,
          pickup_price: dlvPrice?.price || 0,
          dropoff_price: pcpPrice?.price || 0,
          additional_items: v?.additional_items,
          notes: v?.notes,
          price_calculate_data: priceCalculate,
          auto_generate_invoice: "true",
          insurance_id: ["no_extra_protection", "basic_protection"].includes(v?.insurance_id) ? null : v?.insurance_id,
          insurance_code: v?.insurance_code,
          additional_informations: {
            ...v?.additional_informations,
            dateUsage,
            flightReservation: rebuiltFlight,
            hotelReservation: rebuiltHotel,
          },
        };

        const booking = await createBooking.mutateAsync(bookingFormData);
        const newBooking = booking?.data?.data;

        setBookingData(newBooking);
        setCurrentStep(5);

        Notiflix.Loading.remove();
      } catch (error) {
        console.error(error);
        Notiflix.Loading.remove();
        Notiflix.Report.failure("Transaction failed", "Please try again later", "OK");
      } finally {
        setDisabled(false);
      }
    },
  });

  // File upload handlers
  const handleIdentityImage = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!SUPPORTED_FORMATS.includes(file.type)) {
      Notify.failure("Unsupported file format. Please upload JPG or PNG image.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      Notify.failure("File size too large. Maximum allowed size is 20MB.");
      event.target.value = "";
      return;
    }

    setIdentityImage(event.target.files);
    formik.setFieldValue("identity_image", file);
  };

  const handleDrivingLisence = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!SUPPORTED_FORMATS.includes(file.type)) {
      Notify.failure("Unsupported file format. Please upload JPG or PNG image.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      Notify.failure("File size too large. Maximum allowed size is 20MB.");
      event.target.value = "";
      return;
    }

    setDrivingLicense(event.target.files);
    formik.setFieldValue("driving_license", file);
  };

  const handleFamilyCard = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!SUPPORTED_FORMATS.includes(file.type)) {
      Notify.failure("Unsupported file format. Please upload JPG or PNG image.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      Notify.failure("File size too large. Maximum allowed size is 20MB.");
      event.target.value = "";
      return;
    }

    setFamilyCard(event.target.files);
    formik.setFieldValue("family_card", file);
  };

  const handleBusinessId = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!SUPPORTED_FORMATS.includes(file.type)) {
      Notify.failure("Unsupported file format. Please upload JPG or PNG image.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      Notify.failure("File size too large. Maximum allowed size is 20MB.");
      event.target.value = "";
      return;
    }

    setBusinessId(event.target.files);
    formik.setFieldValue("business_id", file);
  };

  const handleHotel = (event) => {
    setHotel(event.target.files);
  };

  const handleFlight = (event) => {
    setFlight(event.target.files);
  };

  // Modal and navigation handlers
  const closeModal = (e) => {
    if (e) e.preventDefault();
    setModal(false);
    setCurrentStep(1);
    setIsSuccess(false);
    setPaymentLink("");
    setBookingData(null);
  };

  const handleSelectIdentity = (e) => {
    const selected = e.target.value;
    setIdentity(selected);
    formik.setFieldValue("identity", selected);
  };

  const handleNavigateBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    } else {
      navigate(-1);
    }
  };

  // Memoized data calculations
  const outOftownDatas = useMemo(() => {
    const result = {};
    dateUsage?.outOfTown?.forEach((item) => {
      const { date, zone } = item;
      if (!result[date]) {
        result[date] = [];
      }
      result[date].push(...zone);
    });
    return result;
  }, [dateUsage]);

  const outOfIslandDatas = useMemo(() => {
    const result = [];
    dateUsage?.outOfIsland?.forEach((item) => {
      result.push(item);
    });
    return result;
  }, [dateUsage]);

  // Recalculate price whenever additional items or date usage changes
  useEffect(() => {
    if (data?.car?.id && formik.values.additional_items?.length > 0) {
      const pickupDate = moment(data?.pickup_date).format("YYYY-MM-DD HH:mm:ss");
      const dropoffDate =
        data?.type !== "transfer_service"
          ? moment(data?.dropoff_date).format("YYYY-MM-DD HH:mm:ss")
          : moment(pickupDate).set({ hour: 23, minute: 59, second: 59 }).format("YYYY-MM-DD HH:mm:ss");

      const body = {
        datetimeFrom: pickupDate,
        type: data?.type,
        carId: data?.car?.id,
        datetimeTo: dropoffDate,
        additionalItems: formik.values.additional_items,
        insurance_id: formik.values.insurance_id,
        insurance_code: formik.values.insurance_code,
      };

      if (data?.type === "self_drive") {
        body.delivery_location = JSON.parse(data?.pickup_location_json);
        body.location = JSON.parse(data?.dropoff_location_json);
        if (outOfIslandDatas) {
          body.outOfIslandDatas = outOfIslandDatas;
        }
      }
      if (data?.type === "tour") {
        body.hour = parseInt(data?.tourPackage);
        body.outOfTownDates = outOftownDatas;
      }
      if (data?.type === "transfer_service") {
        body.location = JSON.parse(data?.dropoff_location_json);
      }

      findPrice.mutateAsync(body).then((res) => {
        const totalCalc = res?.data;
        setPriceCalculate(totalCalc);
      });
    }
  }, [formik.values.additional_items, formik.values.insurance_id, outOftownDatas, outOfIslandDatas]);

  const startDate = moment(data?.pickup_date, "YYYY-MM-DD");
  const endDate =
    data?.type !== "transfer_service"
      ? moment(data?.dropoff_date, "YYYY-MM-DD")
      : moment(data?.pickup_date).add(1, "days").format("YYYY-MM-DD");

  const datesInRange = [];
  let currentDate = startDate.clone();

  while (currentDate.isBefore(endDate)) {
    datesInRange.push(currentDate.format("YYYY-MM-DD"));
    currentDate.add(1, "days");
  }

  useEffect(() => {
    const additionalDatas = datesInRange?.map((val) => {
      return {
        date: val,
        umbrella: 0,
        baby_seat: 0,
        phone_holder: 0,
        gps: 0,
        insurance_id: false,
        all_in_one_additional_items: false,
        wedding: false,
        additional_stop: false,
        with_driver: data?.type === "self_drive" ? data?.conditions?.with_driver : false,
      };
    });
    if (!formik.values?.additional_items?.length) {
      formik.setFieldValue("additional_items", additionalDatas);
    }
  }, [datesInRange, data]);

  // Payment handler
  const updatePaymentLink = async (bookingId, paymentLink) => {
    return await api.put(`/bookings/${bookingId}/payment-link`, {
      payment_link: paymentLink,
    });
  };

  const handleCreatePayment = async () => {
    if (!bookingData) return;

    setDisabled(true);
    Notiflix.Loading.hourglass();

    try {
      const invoiceNumber = bookingData?.booking_invoice?.invoice?.number;

      const xenditData = {
        amount: bookingData?.total_price,
        invoice_number: invoiceNumber,
        reminder_time_unit: "hours",
        reminder_time: 12,
        customer: {
          id: bookingData?.customer?.id,
          email: bookingData?.customer?.email,
          surename: bookingData?.customer?.name,
          phone: bookingData?.customer?.phone,
        },
      };

      const xenditResponse = await createPayment.mutateAsync(xenditData);
      const paymentLink = xenditResponse?.data?.data?.invoice_url;

      setPaymentLink(paymentLink);

      if (paymentLink) {
        await updatePaymentLink(bookingData.id, paymentLink);
      }

      Notiflix.Loading.remove();
      Notiflix.Report.success("Payment Link Created", "Payment link has been updated successfully!", "OK");

      if (paymentLink) {
        window.open(paymentLink, "_blank");
      }

      setIsSuccess(true);
    } catch (error) {
      console.error(error);
      Notiflix.Loading.remove();
      Notiflix.Report.failure("Payment creation failed", "Please try again later", "OK");
    } finally {
      setDisabled(false);
    }
  };

  // Modal effects
  useEffect(() => {
    if (modal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [modal]);

  useEffect(() => {
    if (!modal) {
      formik.resetForm({
        values: {
          ...formik.initialValues,
          additional_items: formik.values?.additional_items || [],
        },
      });
      setIdentity("");
      setIdentityImage(null);
      setDrivingLicense(null);
      setFamilyCard(null);
      setBusinessId(null);
      setHotel(null);
      setFlight(null);
      setCurrentStep(1);
      setIsSuccess(false);
      setPaymentLink("");
      setBookingData(null);
    }
  }, [modal]);

  // Step validation
  const validateStep = async (step) => {
    const errors = await formik.validateForm();

    if (step === 1) {
      // Step 1 tidak memerlukan validasi form
      return true;
    }

    if (step === 2) {
      // Step 2 tidak memerlukan validasi form
      return true;
    }

    if (step === 3) {
      const step3Fields = ["name", "email", "phone", "address"];

      if (data?.type === "self_drive") {
        const requiredFields = [
          "identity",
          "identity_image",
          "driving_license",
          "family_card",
          "social_media",
          "business_id",
        ];
        const hasDocumentErrors = requiredFields.some((field) => errors[field]);
        if (hasDocumentErrors) {
          return false;
        }

        const emergencyErrors = errors.additional_informations;
        if (emergencyErrors?.emergencyContact1 || emergencyErrors?.emergencyContact2) {
          return false;
        }
      }

      const hasErrors = step3Fields.some((field) => errors[field]);
      return !hasErrors;
    }

    if (step === 4) {
      return !errors.privacyPolicy;
    }

    return true;
  };

  // Step navigation
  const handleNext = async () => {
    const isValid = await validateStep(currentStep);

    if (isValid) {
      setCurrentStep((prev) => prev + 1);
    } else {
      if (currentStep === 3) {
        formik.setFieldTouched("name", true);
        formik.setFieldTouched("email", true);
        formik.setFieldTouched("phone", true);
        formik.setFieldTouched("address", true);

        if (data?.type === "self_drive") {
          formik.setFieldTouched("identity", true);
          formik.setFieldTouched("identity_image", true);
          formik.setFieldTouched("driving_license", true);
          formik.setFieldTouched("family_card", true);
          formik.setFieldTouched("social_media", true);
          formik.setFieldTouched("business_id", true);
          formik.setFieldTouched("additional_informations.emergencyContact1", true);
          formik.setFieldTouched("additional_informations.emergencyContact2", true);
        }
      } else if (currentStep === 4) {
        formik.setFieldTouched("privacyPolicy", true);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 4) {
      formik.setFieldValue("privacyPolicy", false);
    }
    setCurrentStep((prev) => prev - 1);
  };

  // Return all states and functions needed by components
  return {
    formik,
    currentStep,
    isSuccess,
    modal,
    closeModal,
    handleNext,
    handleBack,
    disabled,
    data,
    identity,
    setIdentity,
    identityImage,
    setIdentityImage,
    drivingLicense,
    setDrivingLicense,
    familyCard,
    setFamilyCard,
    businessId,
    setBusinessId,
    hotel,
    setHotel,
    flight,
    setFlight,
    priceCalculate,
    bookingData,
    paymentLink,
    handleCreatePayment,
    handleIdentityImage,
    handleDrivingLisence,
    handleFamilyCard,
    handleBusinessId,
    handleHotel,
    handleFlight,
    handleSelectIdentity,
    handleNavigateBack,
    navigate,
    t,
    currentLanguage,
    setIsSuccess,
    insurances: insuranceData?.data || [],
    insuranceLoading,
  };
};

export default useCustomerForm;
